<template>
  <Combobox
    v-model="modelValueWrapper"
    class="w-full min-h-[48px] px-3 py-2 bg-gray-800/5 tablet-h:bg-transparent border border-transparent tablet-h:border-gray-800/25 rounded-sm flex flex-wrap gap-y-1.5 items-center text-[16px] leading-[20px] select-none relative"
    v-slot="{ addItem }"
    ref="combobox"
  >
    <ComboboxItem
      v-for="user in modelValueWrapper"
      :key="user.id"
      v-slot="{ selected }"
    >
      <div
        class="p-1.5 bg-gray-800/5 hover:bg-gra-800/10 font-primary font-medium text-[14px] mr-1.5 rounded-sm cursor-pointer"
        :class="getSelectedClass(selected)"
      >
        {{ user.name }}
      </div>
    </ComboboxItem>
    <ComboboxInput
      class="font-primary text-[14px] outline-hidden h-full grow bg-transparent"
      placeholder="Add an email or name"
      :disabled="props.disabled"
      @change="handleChange"
      @keydown="(e: KeyboardEvent) => handleEnter(e, addItem)"
      @paste="(e: ClipboardEvent) => handlePaste(e, addItem)"
    />
    <div
      v-if="query"
      class="max-h-[200px] overflow-scroll w-full absolute left-0 bg-white rounded-[8px] [box-shadow:0_0_8px_0_rgb(0_0_0_/_10%),_0_2px_3px_0_rgb(0_0_0_/_20%)] z-999"
      ref="resultsRef"
      :style="styles"
      @scroll="handleScroll"
    >
      <div v-if="loading && !users.length">
        <div
          v-for="n in 3"
          :key="n"
          class="w-full h-[48px] px-4 flex items-center first:rounded-tl-[8px] first:rounded-tr-[8px] last:rounded-bl-[8px] last:rounded-br-[8px]"
        >
          <Skeleton class="w-[30px] h-[30px] rounded-full mr-2.5" />
          <div>
            <Skeleton class="w-[200px] h-[14px] mb-1.5" />
            <Skeleton class="w-[140px] h-[12px]" />
          </div>
        </div>
      </div>
      <ComboboxResult
        v-else
        v-for="user in users"
        :key="user.id"
        :value="user"
        v-slot="{ focused }"
      >
        <div
          class="w-full h-[48px] px-4 font-secondary font-medium flex items-center cursor-pointer hover:bg-gray-800/10"
          :class="getActiveClasses(focused)"
        >
          <Avatar
            v-if="user.avatarUrl"
            :size="30"
            :src="user.avatarUrl"
            class="mr-2.5"
          />
          <Icon v-else name="User" :size="30" class="stroke-gray-800 mr-2.5" />
          <div v-truncate class="w-full">
            <div data-truncate-target class="text-[14px]">
              {{ user.name }}
            </div>
            <div data-truncate-target class="text-[12px] opacity-50">
              {{ getSecondaryText(user as Entity) }}
            </div>
          </div>
          <Pill v-if="isYou(user as Entity)" class="ml-auto">You</Pill>
        </div>
      </ComboboxResult>
    </div>
  </Combobox>
</template>

<script setup lang="ts">
import {
  Combobox,
  ComboboxInput,
  ComboboxItem,
  ComboboxResult,
  type ComboboxType,
} from '@/shared/components/Combobox'
import {
  useSearchEntitiesLazyQuery,
  type Entity,
} from '@/shared/graphql/legacy/generated'
import { useUserStore } from '@/shared/stores/user'
import { EMAIL_REGEX } from '@/shared/utils/string'
import { Avatar, Icon, Pill, Skeleton } from '@dacr/ui'
import { computed, nextTick, ref, watch } from 'vue'

const props = defineProps<{
  modelValue: Entity[]
  disabled?: boolean
  showOwnEntity?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', payload: Entity[]): void
}>()

const query = ref('')

const variables = ref({
  query,
  showOwnEntity: props.showOwnEntity || false,
})

const { result, loading, load, fetchMore } =
  useSearchEntitiesLazyQuery(variables)

const userStore = useUserStore()

const combobox = ref<ComboboxType>()
const resultsRef = ref<HTMLElement>()

watch(
  () => props.modelValue,
  () => {
    if (!props.modelValue.length) {
      combobox.value?.reset()
    }
  }
)

const modelValueWrapper = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const styles = computed(() => {
  const boundingClientRect = resultsRef.value?.getBoundingClientRect()

  const topOfContextMenu = boundingClientRect?.top || 0
  const bottomOfContextMenu = topOfContextMenu + 200

  if (bottomOfContextMenu >= window.innerHeight) {
    return {
      bottom: 'calc(0% + 40px)',
    }
  }

  return {
    top: '105%',
  }
})

const getSecondaryText = (item: Entity) => {
  if (item.__typename === 'BasicUser') {
    return item.username
  }

  return `Managed by ${item.owner?.name}`
}

const getSelectedClass = (selected: boolean) => {
  return {
    ...(selected && {
      'bg-green-900!': true,
      'text-white!': true,
    }),
  }
}

const getActiveClasses = (active: boolean) => {
  return {
    outline: active,
    'outline-green-700': active,
    'outline-[3px]': active,
    'outline-offset-[-3px]': active,
  }
}

const handleChange = (value: string) => {
  query.value = value

  load()
}

const isYou = (entity: Entity) => {
  return entity.id === userStore.user?.id
}

const handleEnter = (
  event: KeyboardEvent,
  addItem: (value: unknown) => void
) => {
  if (
    [' ', ',', 'Enter'].includes(event.key) &&
    EMAIL_REGEX.test(query.value)
  ) {
    event.preventDefault()

    addItem({
      id: query.value,
      name: query.value,
    })
  }
}

const handlePaste = (
  event: ClipboardEvent,
  addItem: (value: unknown) => void
) => {
  setTimeout(async () => {
    const string = (event.target as HTMLInputElement).value
    const parts = string.split(' ')

    for (const part of parts) {
      const str = part.trim()

      if (EMAIL_REGEX.test(str)) {
        event.preventDefault()

        addItem({
          id: str,
          name: str,
        })
      }

      await nextTick()
    }
  })
}

const users = computed(() => {
  const alreadySelectedUserIds = props.modelValue.map((user) => user.id)

  const filteredUsers = (result.value?.searchEntities.entities || []).filter(
    (user) => !alreadySelectedUserIds.includes(user.id)
  )

  return filteredUsers
})

const fetchNextPage = () => {
  if (result.value?.searchEntities.next && !loading.value) {
    fetchMore({
      variables: {
        next: result.value?.searchEntities.next,
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query',
          searchEntities: {
            ...previousData.searchEntities,
            entities: [
              ...previousData.searchEntities.entities,
              ...newData.searchEntities.entities,
            ],
            next: newData.searchEntities.next,
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight

  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}
</script>
